/**
 * PricingHistoryTimeline — Visualisation de l'évolution d'un tarif dans le
 * temps pour un tuple (provider, tier, unitType).
 *
 * Affiche :
 *   - Timeline Recharts (line chart priceUsd = f(effectiveFrom))
 *   - Liste des entrées (actives + clôturées)
 */

import { Tag, Timeline } from 'antd';
import type { FC } from 'react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PricingHistoryEntry } from '@/pages/ops/pricing/types';

interface PricingHistoryTimelineProps {
  entries: PricingHistoryEntry[];
  loading?: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatUsd(priceUsd: number): string {
  return priceUsd.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: priceUsd >= 1 ? 2 : 4,
    maximumFractionDigits: 6,
  });
}

export const PricingHistoryTimeline: FC<PricingHistoryTimelineProps> = ({
  entries,
  loading,
}) => {
  if (loading) {
    return (
      <div className="fc-card p-6 text-center text-sm text-slate-500 dark:text-slate-400">
        Chargement de l'historique…
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="fc-card p-6 text-center text-sm text-slate-500 dark:text-slate-400">
        Aucun historique disponible pour ce tuple.
      </div>
    );
  }

  // Préparation données Recharts (ordre chronologique ascendant).
  const chartData = [...entries]
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
    .map((e) => ({
      date: formatDate(e.effectiveFrom),
      priceUsd: e.priceUsd,
      priceCents: e.priceCents,
    }));

  return (
    <div className="space-y-4">
      {/* Graphique */}
      <div className="fc-card p-5">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Évolution du prix
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <XAxis
              dataKey="date"
              stroke="#94a3b8"
              tick={{ fontSize: 11 }}
            />
            <YAxis
              stroke="#94a3b8"
              tick={{ fontSize: 11 }}
              tickFormatter={(v: number) => formatUsd(v)}
            />
            <Tooltip
              formatter={(value) => [
                typeof value === 'number' ? formatUsd(value) : String(value),
                'Prix',
              ]}
              contentStyle={{ borderRadius: 8, fontSize: 12 }}
            />
            <Line
              type="monotone"
              dataKey="priceUsd"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ r: 4, fill: '#3B82F6' }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Timeline */}
      <div className="fc-card p-5">
        <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Entrées ({entries.length})
        </h3>
        <Timeline
          mode="left"
          items={entries.map((e) => ({
            color: e.effectiveTo === null ? 'blue' : 'gray',
            label: formatDate(e.effectiveFrom),
            children: (
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {formatUsd(e.priceUsd)}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    ({e.priceCents.toLocaleString('fr-FR')} cts {e.currency})
                  </span>
                  {e.effectiveTo === null ? (
                    <Tag color="blue">Actif</Tag>
                  ) : (
                    <Tag color="default">
                      Jusqu'au {formatDate(e.effectiveTo)}
                    </Tag>
                  )}
                </div>
                {e.description && (
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {e.description}
                  </p>
                )}
                {e.supersededBy && (
                  <p className="mt-1 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                    Remplacé par {e.supersededBy.slice(0, 8)}…
                  </p>
                )}
              </div>
            ),
          }))}
        />
      </div>
    </div>
  );
};

export default PricingHistoryTimeline;
